import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JpAddressValue } from '../../components/JpAddressInput';
import { maskMyNumber, normalizeMyNumber } from '../../GeneralProfile/useGeneralProfileFlow';
import {
  addressToProfilePatch,
  declarationFromApi,
  fetchTaxDeclarationProgress,
  profileToAddress,
  upsertTaxDeclarationProgress,
} from '../taxDeclarationApi';
import {
  formatAddressDisplay,
  formatDobDisplay,
  formatKanaName,
  formatLegalName,
  formatYesNo,
  headRelationshipLabel,
} from './employeeSectionDisplay';
import {
  firstMissingBranchStep,
  isBranchGateStep,
  isEmployeeSectionComplete,
  isSkippedStep,
  nextStepAfter,
  prevStepForUndo,
  reviewStep,
  stepIndex,
  stepQuestionKey,
} from './employeeSectionSteps';
import {
  EMPLOYEE_SECTION_STEP_ORDER,
  type DeclarationSnapshot,
  type EmployeeSectionStep,
  type HeadRelationship,
  type ProfileSnapshot,
  type YesNo,
} from './employeeSectionTypes';
import { parseIncomeYen, formatIncomeYen } from '../spouseSection/spouseEligibility';

const KANJI_OR_JP_CHARS_RE = /^[\u3040-\u309F\u30A0-\u30FF\u3400-\u9FFF\uF900-\uFAFF\u3000\sー]+$/u;
const KATAKANA_RE = /^[\u30A0-\u30FF\uFF66-\uFF9D\u3000\sー]+$/u;

function emptyProfile(): ProfileSnapshot {
  return {
    familyName: '',
    firstName: '',
    middleName: '',
    familyNameKana: '',
    firstNameKana: '',
    middleNameKana: '',
    dateOfBirth: '',
    myNumber: '',
    address: { postalCode: '', pref: '', city: '', town: '', line2: '' },
  };
}

function emptyDeclaration(): DeclarationSnapshot {
  return {
    isHeadOfHousehold: '',
    headOfHouseholdName: '',
    relationshipToHead: '',
    multipleEmployers: '',
    employeeEstimatedIncome: '',
  };
}

function isAddressComplete(a: JpAddressValue): boolean {
  return (
    a.postalCode.trim().length > 0 &&
    a.pref.trim().length > 0 &&
    a.city.trim().length > 0 &&
    a.town.trim().length > 0
  );
}

function userAnswerForStep(
  step: EmployeeSectionStep,
  profile: ProfileSnapshot,
  decl: DeclarationSnapshot,
  t: (k: string) => string
): string {
  switch (step) {
    case 'fullName':
      return formatLegalName(profile.familyName, profile.firstName, profile.middleName);
    case 'fullNameKana':
      return formatKanaName(profile.familyNameKana, profile.firstNameKana, profile.middleNameKana);
    case 'dob':
      return formatDobDisplay(profile.dateOfBirth);
    case 'myNumber':
      return maskMyNumber(profile.myNumber);
    case 'address':
      return formatAddressDisplay(profile.address);
    case 'headOfHousehold':
      return formatYesNo(t, decl.isHeadOfHousehold);
    case 'headName':
      return decl.headOfHouseholdName.trim() || '—';
    case 'headRelationship':
      return headRelationshipLabel(t, decl.relationshipToHead);
    case 'employeeEstimatedIncome': {
      const n = parseIncomeYen(decl.employeeEstimatedIncome);
      return n > 0 ? `${formatIncomeYen(n)} yen` : '—';
    }
    case 'multipleEmployers':
      return formatYesNo(t, decl.multipleEmployers);
    case 'multipleEmployersNotice':
      return t('submitDocuments.dependentDeduction.employee.multipleEmployersAck');
    default:
      return '';
  }
}

export function useEmployeeSectionFlow(
  email: string | null | undefined,
  taxYear: number,
  t: (key: string) => string,
  onSectionDoneChange?: (done: boolean) => void
) {
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState(false);
  const [step, setStep] = useState<EmployeeSectionStep>('fullName');
  const [profile, setProfile] = useState<ProfileSnapshot>(emptyProfile);
  const [decl, setDecl] = useState<DeclarationSnapshot>(emptyDeclaration);
  const [dob, setDob] = useState({ year: 1990, month: 1, day: 1 });

  const [fullNameCommitted, setFullNameCommitted] = useState(false);
  const [fullNameKanaCommitted, setFullNameKanaCommitted] = useState(false);
  const [dobCommitted, setDobCommitted] = useState(false);
  const [myNumberCommitted, setMyNumberCommitted] = useState(false);
  const [addressCommitted, setAddressCommitted] = useState(false);
  const [headOfHouseholdCommitted, setHeadOfHouseholdCommitted] = useState(false);
  const [headNameCommitted, setHeadNameCommitted] = useState(false);
  const [headRelationshipCommitted, setHeadRelationshipCommitted] = useState(false);
  const [employeeIncomeCommitted, setEmployeeIncomeCommitted] = useState(false);
  const [multipleEmployersCommitted, setMultipleEmployersCommitted] = useState(false);
  const [noticeAcknowledged, setNoticeAcknowledged] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [editReturnStep, setEditReturnStep] = useState<'review' | null>(null);
  const [editBranchGate, setEditBranchGate] = useState<EmployeeSectionStep | null>(null);
  const editingFromReview = editReturnStep === 'review';

  const applyCommittedThrough = useCallback((target: EmployeeSectionStep, declSnap: DeclarationSnapshot) => {
    const idx = stepIndex(target);
    setFullNameCommitted(idx > stepIndex('fullName'));
    setFullNameKanaCommitted(idx > stepIndex('fullNameKana'));
    setDobCommitted(idx > stepIndex('dob'));
    setMyNumberCommitted(idx > stepIndex('myNumber'));
    setAddressCommitted(idx > stepIndex('address'));
    setHeadOfHouseholdCommitted(idx > stepIndex('headOfHousehold'));
    setHeadNameCommitted(
      idx > stepIndex('headName') && !isSkippedStep('headName', declSnap)
    );
    setHeadRelationshipCommitted(
      idx > stepIndex('headRelationship') && !isSkippedStep('headRelationship', declSnap)
    );
    setEmployeeIncomeCommitted(idx > stepIndex('employeeEstimatedIncome'));
    setMultipleEmployersCommitted(idx > stepIndex('multipleEmployers'));
    setNoticeAcknowledged(
      idx > stepIndex('multipleEmployersNotice') && !isSkippedStep('multipleEmployersNotice', declSnap)
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!email?.trim()) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const data = await fetchTaxDeclarationProgress(email.trim(), taxYear);
      if (cancelled) return;

      if (data?.profile) {
        const p = data.profile;
        const loaded: ProfileSnapshot = {
          familyName: p.familyName ?? '',
          firstName: p.firstName ?? '',
          middleName: p.middleName ?? '',
          familyNameKana: p.familyNameKana ?? '',
          firstNameKana: p.firstNameKana ?? '',
          middleNameKana: p.middleNameKana ?? '',
          dateOfBirth: p.dateOfBirth ?? '',
          myNumber: p.myNumber ?? '',
          address: profileToAddress(p),
        };
        setProfile(loaded);
        const m = loaded.dateOfBirth.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (m) {
          setDob({ year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) });
        }
      }

      const loadedDecl = declarationFromApi(data?.declaration ?? {});
      setDecl(loadedDecl);

      const cursor = typeof data?.currentStepId === 'string' ? data.currentStepId.trim() : '';
      const validStep = EMPLOYEE_SECTION_STEP_ORDER.includes(cursor as EmployeeSectionStep)
        ? (cursor as EmployeeSectionStep)
        : 'fullName';

      if (data?.sectionEmployeeDone) {
        const terminal = reviewStep(loadedDecl);
        setStep(terminal);
        applyCommittedThrough(terminal, loadedDecl);
        setReviewMode(true);
      } else if (cursor) {
        setStep(validStep);
        applyCommittedThrough(validStep, loadedDecl);
      } else {
        setStep('fullName');
        applyCommittedThrough('fullName', loadedDecl);
      }

      setIsLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [email, taxYear, applyCommittedThrough]);

  const isComplete = useMemo(() => {
    if (decl.multipleEmployers === 'yes') return noticeAcknowledged;
    return multipleEmployersCommitted && decl.multipleEmployers === 'no';
  }, [decl.multipleEmployers, multipleEmployersCommitted, noticeAcknowledged]);

  const persist = useCallback(
    async (opts: {
      nextStep?: EmployeeSectionStep | null;
      profilePatch?: Partial<ProfileSnapshot>;
      declarationPatch?: Partial<DeclarationSnapshot>;
      sectionDone?: boolean;
    }) => {
      if (!email?.trim()) return false;
      const nextProfile = { ...profile, ...opts.profilePatch };
      const nextDecl = { ...decl, ...opts.declarationPatch };
      if (opts.profilePatch) setProfile(nextProfile);
      if (opts.declarationPatch) setDecl(nextDecl);

      const profilePatchApi =
        opts.profilePatch &&
        ({
          familyName: nextProfile.familyName.trim(),
          firstName: nextProfile.firstName.trim(),
          middleName: nextProfile.middleName.trim(),
          familyNameKana: nextProfile.familyNameKana.trim(),
          firstNameKana: nextProfile.firstNameKana.trim(),
          middleNameKana: nextProfile.middleNameKana.trim(),
          dateOfBirth: nextProfile.dateOfBirth.trim() || undefined,
          myNumber: normalizeMyNumber(nextProfile.myNumber),
          ...addressToProfilePatch(nextProfile.address),
        } as const);

      const saved = await upsertTaxDeclarationProgress({
        email: email.trim(),
        taxYear,
        ...(opts.nextStep !== undefined ? { currentStepId: opts.nextStep } : {}),
        ...(opts.sectionDone !== undefined ? { sectionEmployeeDone: opts.sectionDone } : {}),
        profilePatch: profilePatchApi,
        declaration: nextDecl,
      });
      if (!saved) {
        setSaveError(true);
        return false;
      }
      setSaveError(false);
      if (opts.sectionDone === true) onSectionDoneChange?.(true);
      if (opts.sectionDone === false) onSectionDoneChange?.(false);
      return true;
    },
    [decl, email, onSectionDoneChange, profile, taxYear]
  );

  const returnToReview = useCallback(
    async (declSnap?: DeclarationSnapshot) => {
      const d = declSnap ?? decl;
      const terminal = reviewStep(d);
      setEditReturnStep(null);
      setEditBranchGate(null);
      setReviewMode(true);
      setStep(terminal);
      await persist({ nextStep: terminal, sectionDone: true });
    },
    [decl, persist]
  );

  const finishStepOrReturnToReview = useCallback(
    async (params: {
      fromStep: EmployeeSectionStep;
      nextForward: EmployeeSectionStep | null;
      profilePatch?: Partial<ProfileSnapshot>;
      declPatch?: Partial<DeclarationSnapshot>;
      noticeAcknowledgedOverride?: boolean;
    }) => {
      const mergedDecl = { ...decl, ...params.declPatch };
      const noticeOk =
        params.noticeAcknowledgedOverride !== undefined
          ? params.noticeAcknowledgedOverride
          : noticeAcknowledged;

      if (editReturnStep === 'review') {
        const gate =
          editBranchGate ?? (isBranchGateStep(params.fromStep) ? params.fromStep : null);
        const ok = await persist({
          profilePatch: params.profilePatch,
          declarationPatch: params.declPatch,
          sectionDone: false,
        });
        if (!ok) return;

        if (gate) {
          if (!editBranchGate) setEditBranchGate(gate);
          const missing = firstMissingBranchStep(gate, mergedDecl, { noticeAcknowledged: noticeOk });
          if (missing) {
            setStep(missing);
            await persist({ nextStep: missing, sectionDone: false });
            return;
          }
        }

        await returnToReview(mergedDecl);
        return;
      }

      const sectionDone =
        params.nextForward === null && isEmployeeSectionComplete(step, mergedDecl);
      const ok = await persist({
        nextStep: params.nextForward,
        profilePatch: params.profilePatch,
        declarationPatch: params.declPatch,
        sectionDone: sectionDone || undefined,
      });
      if (!ok) return;
      if (sectionDone) setReviewMode(true);
      if (params.nextForward) setStep(params.nextForward);
    },
    [decl, editBranchGate, editReturnStep, noticeAcknowledged, persist, returnToReview, step]
  );

  const setCommittedForStep = useCallback((s: EmployeeSectionStep) => {
    switch (s) {
      case 'fullName':
        setFullNameCommitted(true);
        break;
      case 'fullNameKana':
        setFullNameKanaCommitted(true);
        break;
      case 'dob':
        setDobCommitted(true);
        break;
      case 'myNumber':
        setMyNumberCommitted(true);
        break;
      case 'address':
        setAddressCommitted(true);
        break;
      case 'headOfHousehold':
        setHeadOfHouseholdCommitted(true);
        break;
      case 'headName':
        setHeadNameCommitted(true);
        break;
      case 'headRelationship':
        setHeadRelationshipCommitted(true);
        break;
      case 'employeeEstimatedIncome':
        setEmployeeIncomeCommitted(true);
        break;
      case 'multipleEmployers':
        setMultipleEmployersCommitted(true);
        break;
      case 'multipleEmployersNotice':
        setNoticeAcknowledged(true);
        break;
      default:
        break;
    }
  }, []);

  const handleSendFullName = useCallback(async () => {
    if (!profile.familyName.trim() || !profile.firstName.trim()) return;
    setCommittedForStep('fullName');
    await finishStepOrReturnToReview({
      fromStep: 'fullName',
      nextForward: 'fullNameKana',
      profilePatch: {
        familyName: profile.familyName,
        firstName: profile.firstName,
        middleName: profile.middleName,
      },
    });
  }, [finishStepOrReturnToReview, profile, setCommittedForStep]);

  const handleSendFullNameKana = useCallback(async () => {
    if (!profile.familyNameKana.trim() || !profile.firstNameKana.trim()) return;
    setCommittedForStep('fullNameKana');
    await finishStepOrReturnToReview({
      fromStep: 'fullNameKana',
      nextForward: 'dob',
      profilePatch: {
        familyNameKana: profile.familyNameKana,
        firstNameKana: profile.firstNameKana,
        middleNameKana: profile.middleNameKana,
      },
    });
  }, [finishStepOrReturnToReview, profile, setCommittedForStep]);

  const handleSendDob = useCallback(async () => {
    const iso = `${dob.year}-${String(dob.month).padStart(2, '0')}-${String(dob.day).padStart(2, '0')}`;
    setCommittedForStep('dob');
    setProfile((p) => ({ ...p, dateOfBirth: iso }));
    await finishStepOrReturnToReview({
      fromStep: 'dob',
      nextForward: 'myNumber',
      profilePatch: { dateOfBirth: iso },
    });
  }, [dob.day, dob.month, dob.year, finishStepOrReturnToReview, setCommittedForStep]);

  const handleSendMyNumber = useCallback(async () => {
    const n = normalizeMyNumber(profile.myNumber);
    if (n.length !== 12) return;
    setCommittedForStep('myNumber');
    await finishStepOrReturnToReview({
      fromStep: 'myNumber',
      nextForward: 'address',
      profilePatch: { myNumber: n },
    });
  }, [finishStepOrReturnToReview, profile.myNumber, setCommittedForStep]);

  const handleSendAddress = useCallback(async () => {
    if (!isAddressComplete(profile.address)) return;
    setCommittedForStep('address');
    await finishStepOrReturnToReview({
      fromStep: 'address',
      nextForward: 'headOfHousehold',
      profilePatch: { address: profile.address },
    });
  }, [finishStepOrReturnToReview, profile.address, setCommittedForStep]);

  const handleHeadOfHousehold = useCallback(
    async (v: YesNo) => {
      const patch: Partial<DeclarationSnapshot> = {
        isHeadOfHousehold: v,
        headOfHouseholdName: v === 'yes' ? '' : decl.headOfHouseholdName,
        relationshipToHead: v === 'yes' ? '' : decl.relationshipToHead,
      };
      setCommittedForStep('headOfHousehold');
      if (v === 'yes') {
        setHeadNameCommitted(false);
        setHeadRelationshipCommitted(false);
      }
      if (editReturnStep === 'review') {
        setEditBranchGate('headOfHousehold');
      }
      const next = nextStepAfter('headOfHousehold', { ...decl, ...patch });
      await finishStepOrReturnToReview({ fromStep: 'headOfHousehold', nextForward: next, declPatch: patch });
    },
    [decl, editReturnStep, finishStepOrReturnToReview, setCommittedForStep]
  );

  const handleSendHeadName = useCallback(async () => {
    if (!decl.headOfHouseholdName.trim()) return;
    setCommittedForStep('headName');
    await finishStepOrReturnToReview({
      fromStep: 'headName',
      nextForward: 'headRelationship',
      declPatch: { headOfHouseholdName: decl.headOfHouseholdName },
    });
  }, [decl.headOfHouseholdName, finishStepOrReturnToReview, setCommittedForStep]);

  const handleHeadRelationship = useCallback(
    async (v: HeadRelationship) => {
      setCommittedForStep('headRelationship');
      await finishStepOrReturnToReview({
        fromStep: 'headRelationship',
        nextForward: 'employeeEstimatedIncome',
        declPatch: { relationshipToHead: v },
      });
    },
    [finishStepOrReturnToReview, setCommittedForStep]
  );

  const handleEmployeeIncome = useCallback(async () => {
    if (parseIncomeYen(decl.employeeEstimatedIncome) <= 0) return;
    setCommittedForStep('employeeEstimatedIncome');
    await finishStepOrReturnToReview({
      fromStep: 'employeeEstimatedIncome',
      nextForward: 'multipleEmployers',
      declPatch: { employeeEstimatedIncome: decl.employeeEstimatedIncome },
    });
  }, [decl.employeeEstimatedIncome, finishStepOrReturnToReview, setCommittedForStep]);

  const handleMultipleEmployers = useCallback(
    async (v: YesNo) => {
      setReviewMode(false);
      setCommittedForStep('multipleEmployers');
      let noticeOk = noticeAcknowledged;
      if (v === 'no') {
        setNoticeAcknowledged(false);
        noticeOk = false;
      } else if (editReturnStep === 'review' && decl.multipleEmployers !== 'yes') {
        setNoticeAcknowledged(false);
        noticeOk = false;
      }
      if (editReturnStep === 'review') {
        setEditBranchGate('multipleEmployers');
      }
      const next = nextStepAfter('multipleEmployers', { ...decl, multipleEmployers: v });
      await finishStepOrReturnToReview({
        fromStep: 'multipleEmployers',
        nextForward: next,
        declPatch: { multipleEmployers: v },
        noticeAcknowledgedOverride: noticeOk,
      });
    },
    [decl, editReturnStep, finishStepOrReturnToReview, noticeAcknowledged, setCommittedForStep]
  );

  const handleAcknowledgeNotice = useCallback(async () => {
    setCommittedForStep('multipleEmployersNotice');
    if (editReturnStep === 'review') {
      await returnToReview({ ...decl, multipleEmployers: 'yes' });
      return;
    }
    setReviewMode(true);
    await persist({ nextStep: null, sectionDone: true });
  }, [decl, editReturnStep, persist, returnToReview, setCommittedForStep]);

  const beginEditField = useCallback(
    async (fieldStep: EmployeeSectionStep) => {
      setEditReturnStep('review');
      setEditBranchGate(isBranchGateStep(fieldStep) ? fieldStep : null);
      setReviewMode(false);
      setStep(fieldStep);
      await persist({ nextStep: fieldStep, sectionDone: false });
    },
    [persist]
  );

  const handleUndo = useCallback(async () => {
    const prev = prevStepForUndo(step, decl);
    if (!prev) return;
    setReviewMode(false);
    setStep(prev);
    applyCommittedThrough(prev, decl);
    await persist({ nextStep: prev, sectionDone: false });
  }, [applyCommittedThrough, decl, persist, step]);

  const enterReviewFromMenu = useCallback(() => {
    if (!isComplete) return;
    const terminal = reviewStep(decl);
    setReviewMode(true);
    setStep(terminal);
    applyCommittedThrough(terminal, decl);
  }, [applyCommittedThrough, decl, isComplete]);

  const validateLegalName = useCallback(
    (v: string) => {
      const trimmed = v.trim();
      if (!trimmed) return t('submitDocuments.profileName.validation.required');
      if (!KANJI_OR_JP_CHARS_RE.test(trimmed)) return t('submitDocuments.profileName.validation.kanji');
      return null;
    },
    [t]
  );

  const validateKana = useCallback((v: string) => {
    const s = v.trim();
    if (!s) return t('submitDocuments.profileName.validation.required');
    if (!KATAKANA_RE.test(s)) return t('submitDocuments.profileName.validation.kana');
    return null;
  }, [t]);

  const validateMyNumber = useCallback((v: string) => {
    if (normalizeMyNumber(v).length !== 12) {
      return t('submitDocuments.profileName.validation.myNumber');
    }
    return null;
  }, [t]);

  const showUndoFullName = step === 'fullNameKana' && fullNameCommitted;
  const showUndoFullNameKana = step === 'dob' && fullNameKanaCommitted;
  const showUndoDob = step === 'myNumber' && dobCommitted;
  const showUndoMyNumber = step === 'address' && myNumberCommitted;
  const showUndoAddress = step === 'headOfHousehold' && addressCommitted;
  const showUndoHeadOfHousehold =
    (step === 'headName' || (step === 'employeeEstimatedIncome' && decl.isHeadOfHousehold === 'yes')) &&
    headOfHouseholdCommitted;
  const showUndoHeadName = step === 'headRelationship' && headNameCommitted;
  const showUndoHeadRelationship = step === 'employeeEstimatedIncome' && headRelationshipCommitted;
  const showUndoEmployeeIncome = step === 'multipleEmployers' && employeeIncomeCommitted;
  const showUndoMultipleEmployers =
    (step === 'multipleEmployersNotice' || (step === 'multipleEmployers' && multipleEmployersCommitted)) &&
    multipleEmployersCommitted;

  const canSendFullName = profile.familyName.trim().length > 0 && profile.firstName.trim().length > 0;
  const canSendFullNameKana =
    profile.familyNameKana.trim().length > 0 && profile.firstNameKana.trim().length > 0;
  const canSendMyNumber = normalizeMyNumber(profile.myNumber).length === 12;
  const canSendAddress = isAddressComplete(profile.address);
  const canSendHeadName = decl.headOfHouseholdName.trim().length > 0;
  const canSendEmployeeIncome = parseIncomeYen(decl.employeeEstimatedIncome) > 0;

  return {
    t,
    step,
    stepQuestionKey,
    userAnswerForStep: (s: EmployeeSectionStep) => userAnswerForStep(s, profile, decl, t),
    isLoading,
    saveError,
    isComplete,
    profile,
    setProfile,
    decl,
    setDecl,
    dob,
    setDob,
    fullNameCommitted,
    fullNameKanaCommitted,
    dobCommitted,
    myNumberCommitted,
    addressCommitted,
    headOfHouseholdCommitted,
    headNameCommitted,
    headRelationshipCommitted,
    employeeIncomeCommitted,
    multipleEmployersCommitted,
    noticeAcknowledged,
    handleSendFullName,
    handleSendFullNameKana,
    handleSendDob,
    handleSendMyNumber,
    handleSendAddress,
    handleHeadOfHousehold,
    handleSendHeadName,
    handleHeadRelationship,
    handleEmployeeIncome,
    handleMultipleEmployers,
    handleAcknowledgeNotice,
    handleUndo,
    beginEditField,
    enterReviewFromMenu,
    reviewMode,
    editingFromReview,
    undoLabel: t('submitDocuments.chat.undo'),
    editLabel: t('submitDocuments.fillFormsChecklist.edit'),
    validateLegalName,
    validateKana,
    validateMyNumber,
    canSendFullName,
    canSendFullNameKana,
    canSendMyNumber,
    canSendAddress,
    canSendHeadName,
    canSendEmployeeIncome,
    showUndoFullName,
    showUndoFullNameKana,
    showUndoDob,
    showUndoMyNumber,
    showUndoAddress,
    showUndoHeadOfHousehold,
    showUndoHeadName,
    showUndoHeadRelationship,
    showUndoEmployeeIncome,
    showUndoMultipleEmployers,
    markSectionComplete: handleAcknowledgeNotice,
  };
}

export type EmployeeSectionFlow = ReturnType<typeof useEmployeeSectionFlow>;
